import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardLayout, Card, Grid, CardIcon, CardTitle, CardValue, CardDescription } from '../../components/DashboardLayout';
import glassTheme from '../../styles/glassTheme';
import { dashboardApi, StudentDashboardData } from '../../services/dashboardApi';
import ConsentPrompt from '../../components/ConsentPrompt';

/**
 * The learner's primary action: resume exactly where they left off.
 * Placed above the statistics deliberately - the dashboard answers
 * "what do I do next?" before "how am I doing?".
 */
const ContinueCard = styled(Card)`
  cursor: pointer;
  margin-bottom: 1.5rem;
  border: 1px solid ${glassTheme.colors.accent.cyan}55;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px ${glassTheme.colors.accent.cyan}33;
  }
`;

const ContinueLabel = styled.div`
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${glassTheme.colors.accent.cyan};
  margin-bottom: 0.5rem;
`;

const ContinueTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const ContinueMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
`;

const ProgressCard = styled(Card)`
  grid-column: span 2;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: 100%;
  height: 20px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  overflow: hidden;
  margin: 1rem 0;

  &::after {
    content: '';
    display: block;
    width: ${props => props.progress}%;
    height: 100%;
    background: ${glassTheme.gradients.cyber};
    border-radius: ${glassTheme.borderRadius.md};
    transition: width 1s ease;
    box-shadow: 0 0 10px ${glassTheme.colors.accent.blue};
  }
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const ActivityList = styled.div`
  margin-top: 1rem;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  margin-bottom: 0.5rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(0, 0, 0, 0.3);
    border-color: ${glassTheme.colors.borderHover};
    transform: translateX(5px);
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${glassTheme.gradients.cyber};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.2rem;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  color: ${glassTheme.colors.text.primary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ActivityTime = styled.div`
  color: ${glassTheme.colors.text.muted};
  font-size: 0.85rem;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

const ActionButton = styled.button`
  padding: 1.5rem;
  background: ${glassTheme.gradients.cyber};
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
    filter: brightness(1.1);
  }

  i {
    font-size: 2rem;
  }
`;

const WelcomeCard = styled(Card)`
  grid-column: 1 / -1;
  background: rgba(96, 165, 250, 0.15);
  border: 1px solid ${glassTheme.colors.accent.blue};
`;

const CoursesSection = styled.div`
  margin-top: 2rem;
`;

const CoursesTitle = styled.h2`
  color: ${glassTheme.colors.text.primary};
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const CourseCard = styled.div`
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
  padding: 1.5rem;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    border-color: ${glassTheme.colors.borderHover};
    transform: translateY(-2px);
  }
`;

const CourseTitleText = styled.h3`
  color: ${glassTheme.colors.text.primary};
  font-size: 1.1rem;
  margin: 0 0 0.5rem 0;
`;

const CourseDescription = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
`;

const CourseMeta = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.75rem;
  color: ${glassTheme.colors.text.muted};
  font-size: 0.85rem;
`;

const LoadingText = styled.div`
  color: ${glassTheme.colors.text.secondary};
  text-align: center;
  padding: 2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: ${glassTheme.colors.text.muted};
`;

interface Course {
  _id: string;
  courseCode: string;
  title: string;
  description: string;
  teacher: any;
  chapters: any[];
  totalTopics: number;
}

export const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<StudentDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      setData(await dashboardApi.getStudent());
    } catch (err) {
      console.error('Failed to load student dashboard:', err);
      setError('无法加载学习数据，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const enrolledCourses = data?.courses || [];
  const stats = data?.stats;
  const cont = data?.continueLearning;

  const activityIcon = (type: string) =>
    type === 'reading' ? '📖'
      : type === 'exercise' ? '✏️'
        : type === 'selfExplanation' ? '💭'
          : type === 'video' ? '🎬' : '📌';

  const activityLabel = (type: string) =>
    type === 'reading' ? '完成了阅读'
      : type === 'exercise' ? '完成了练习'
        : type === 'selfExplanation' ? '完成了自我解释'
          : type === 'video' ? '观看了视频' : '完成了学习活动';

  const relativeTime = (iso: string) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${Math.max(mins, 1)} 分钟前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} 小时前`;
    return `${Math.floor(hours / 24)} 天前`;
  };

  const handleStartCourse = (courseId: string) => {
    navigate(`/student/course/${courseId}`);
  };

  return (
    <DashboardLayout
      title="欢迎回来！"
      subtitle="继续您的统计学学习之旅"
    >
      {error && (
        <EmptyState style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
          <div>{error}</div>
        </EmptyState>
      )}

      {/* Research consent. Without this the participant has no way to decide,
          and no research use of their data would be defensible. */}
      <ConsentPrompt />

      {/* Primary action first: resume exactly where the learner left off. */}
      {cont && (
        <ContinueCard onClick={() => handleStartCourse(cont.courseId)}>
          <ContinueLabel>继续学习</ContinueLabel>
          <ContinueTitle>{cont.topicTitle || cont.courseTitle}</ContinueTitle>
          <ContinueMeta>
            <span>{cont.courseTitle}</span>
            <span>·</span>
            <span>进度 {cont.progress}%</span>
            {cont.lastAccessAt && <><span>·</span><span>{relativeTime(cont.lastAccessAt)}</span></>}
          </ContinueMeta>
          <ProgressBar progress={cont.progress} style={{ marginTop: '1rem' }} />
        </ContinueCard>
      )}

      {/* What is due next. Only rendered when deadlines exist, so the view
          stays quiet until an instructor publishes a schedule. */}
      {(data?.upcomingDeadlines.length || 0) > 0 && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardTitle>即将到期</CardTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' }}>
            {data!.upcomingDeadlines.map(d => (
              <div
                key={d._id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.6rem 0.75rem',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.03)',
                  borderLeft: `4px solid ${d.daysUntil <= 1 ? glassTheme.colors.status.error : glassTheme.colors.status.warning}`
                }}
              >
                <div>
                  <div style={{ color: glassTheme.colors.text.primary }}>
                    {d.isHard ? '🔒 ' : '📅 '}{d.title}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: glassTheme.colors.text.muted }}>
                    {d.courseTitle}
                  </div>
                </div>
                <div style={{ fontSize: '0.85rem', color: glassTheme.colors.text.secondary, whiteSpace: 'nowrap' }}>
                  {d.daysUntil <= 0 ? '今天截止' : `${d.daysUntil} 天后截止`}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Grid>
        <WelcomeCard>
          <CardIcon>👋</CardIcon>
          <CardTitle>学习进度概览</CardTitle>
          <ProgressLabel>
            <span>总体进度</span>
            <span>{stats ? stats.averageProgress : 0}%</span>
          </ProgressLabel>
          <ProgressBar progress={stats ? stats.averageProgress : 0} />
          <CardDescription>
            {loading ? '加载中...'
              : !stats || stats.averageProgress < 30 ? '刚开始学习，继续保持！'
                : stats.averageProgress < 70 ? '进展顺利，再接再厉！'
                  : '即将完成，太棒了！'}
          </CardDescription>
        </WelcomeCard>
        <Card>
          <CardIcon>📖</CardIcon>
          <CardValue>{stats ? stats.readingCompleted : 0}</CardValue>
          <CardTitle>已完成阅读</CardTitle>
          <CardDescription>完成的阅读活动数量</CardDescription>
        </Card>
        <Card>
          <CardIcon>✍️</CardIcon>
          <CardValue>{stats ? stats.exercisesCompleted : 0}</CardValue>
          <CardTitle>已完成练习</CardTitle>
          <CardDescription>完成的练习题数量</CardDescription>
        </Card>
        <Card>
          <CardIcon>💭</CardIcon>
          <CardValue>{stats ? stats.selfExplanationsCompleted : 0}</CardValue>
          <CardTitle>自我解释</CardTitle>
          <CardDescription>完成的自我解释数量</CardDescription>
        </Card>
        <Card>
          <CardIcon>⏱️</CardIcon>
          <CardValue>{stats ? stats.studyTimeMinutes : 0}</CardValue>
          <CardTitle>学习时长（分钟）</CardTitle>
          <CardDescription>累计学习时间</CardDescription>
        </Card>
        <Card>
          <CardIcon>🔥</CardIcon>
          <CardValue>{stats ? stats.streakDays : 0}</CardValue>
          <CardTitle>连续学习天数</CardTitle>
          <CardDescription>保持学习热情</CardDescription>
        </Card>
        <ProgressCard>
          <CardIcon>📊</CardIcon>
          <CardTitle>最近活动</CardTitle>
          {loading ? (
            <LoadingText>加载中...</LoadingText>
          ) : (data?.recentActivity.length || 0) === 0 ? (
            <CardDescription>暂无学习记录，开始你的第一个活动吧</CardDescription>
          ) : (
            <ActivityList>
              {data!.recentActivity.map((activity, index) => (
                <ActivityItem key={index}>
                  <ActivityIcon>{activityIcon(activity.activityType)}</ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>
                      {activityLabel(activity.activityType)}
                      {activity.topicTitle ? `：${activity.topicTitle}` : ''}
                    </ActivityTitle>
                    <ActivityTime>{relativeTime(activity.completedAt)}</ActivityTime>
                  </ActivityContent>
                </ActivityItem>
              ))}
            </ActivityList>
          )}
        </ProgressCard>
      </Grid>
      <CoursesSection>
        <CoursesTitle>我的课程 ({enrolledCourses.length})</CoursesTitle>
        {loading ? (
          <LoadingText>加载中...</LoadingText>
        ) : enrolledCourses.length === 0 ? (
          <EmptyState>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📖</div>
            <div>暂未注册任何课程</div>
            <ActionButton onClick={() => navigate('/learn')} style={{ marginTop: '1rem' }}>
              <i>📚</i>
              <span>去选课</span>
            </ActionButton>
          </EmptyState>
        ) : (
          <CourseGrid>
            {enrolledCourses.map(course => (
              <CourseCard key={course._id} onClick={() => handleStartCourse(course._id)}>
                <CourseTitleText>{course.title}</CourseTitleText>
                <CourseDescription>
                  {course.description?.substring(0, 100)}{course.description?.length > 100 ? '...' : ''}
                </CourseDescription>
                <ProgressLabel style={{ marginTop: '0.75rem' }}>
                  <span>我的进度</span>
                  <span>{course.progress}%</span>
                </ProgressLabel>
                <ProgressBar progress={course.progress} />
                <CourseMeta>
                  <span>📝 {course.totalTopics || 0} 知识点</span>
                  <span>👨‍🏫 {course.teacherName || '教师'}</span>
                </CourseMeta>
              </CourseCard>
            ))}
          </CourseGrid>
        )}
      </CoursesSection>
      <Card style={{ marginTop: '2rem' }}>
        <CardTitle>快速开始</CardTitle>
        <QuickActions>
          <ActionButton onClick={() => navigate('/learn')}>
            <i>📚</i>
            <span>浏览课程</span>
          </ActionButton>
          <ActionButton onClick={() => navigate('/learn')}>
            <i>✏️</i>
            <span>练习题</span>
          </ActionButton>
          <ActionButton onClick={() => navigate('/learn')}>
            <i>📊</i>
            <span>数据分析</span>
          </ActionButton>
          <ActionButton onClick={() => navigate('/learn')}>
            <i>🎓</i>
            <span>统计助教</span>
          </ActionButton>
        </QuickActions>
      </Card>
    </DashboardLayout>
  );
};
